import { Show, For, createEffect, type Component } from "solid-js";
import { createSignal } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { formatCell } from "@/shared/format-utils";
import { registerPanel } from "@/panel-registry";
import { PanelChrome } from "../console/ui";
import { Drawer } from "@/shared/drawer";
import "./user-admin.css";

interface AdUser {
  SamAccountName?: string;
  Name?: string;
  UserPrincipalName?: string;
  Enabled?: boolean;
  LockedOut?: boolean;
  LastLogon?: string;
  Description?: string;
}

interface AdGroup {
  Name?: string;
  SamAccountName?: string;
  Category?: string;
  Scope?: string;
  Description?: string;
}

interface AdComputer {
  Name?: string;
  DNSHostName?: string;
  OperatingSystem?: string;
  OperatingSystemVersion?: string;
  Enabled?: boolean;
  LastLogon?: string;
}

interface AdOu {
  Name?: string;
  DistinguishedName?: string;
  Description?: string;
  WhenCreated?: string;
}

interface AdGpo {
  DisplayName?: string;
  GpoStatus?: string;
  CreationTime?: string;
  ModificationTime?: string;
  Owner?: string;
}

interface ListResult<T> {
  rows: T[];
  count?: number;
  limit?: number;
}

const PASSWORD_RE = /^[\x21-\x7E]{8,256}$/;

const AdPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [filter, setFilter] = createSignal("");
  const [limit, setLimit] = createSignal(100);
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  // Drawer de réinitialisation de mot de passe.
  const [pwdTarget, setPwdTarget] = createSignal<AdUser | null>(null);
  // Sélection multiple (bulk).
  const [selected, setSelected] = createSignal<Record<string, boolean>>({});
  const [bulkProgress, setBulkProgress] = createSignal<{ done: number; total: number } | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const usersQuery = createToolQuery<ListResult<AdUser>>(() => ({
    tool: "ad_users_list",
    params: { filter: filter(), limit: limit() },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const groupsQuery = createToolQuery<ListResult<AdGroup>>(() => ({
    tool: "ad_groups_list",
    params: { filter: filter(), limit: limit() },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const computersQuery = createToolQuery<ListResult<AdComputer>>(() => ({
    tool: "ad_computers_list",
    params: { filter: filter(), limit: limit() },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const ousQuery = createToolQuery<ListResult<AdOu>>(() => ({
    tool: "ad_ous_list",
    params: { filter: filter(), limit: limit() },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const gposQuery = createToolQuery<ListResult<AdGpo>>(() => ({
    tool: "ad_gpos_list",
    params: { filter: filter(), limit: limit() },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const userRows = (): AdUser[] => usersQuery.data?.rows ?? [];
  const selectedRows = (): AdUser[] => userRows().filter((r) => !!selected()[r.SamAccountName ?? ""]);

  function setRowSelected(sam: string, on: boolean): void {
    if (!sam) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (on) next[sam] = true;
      else delete next[sam];
      return next;
    });
  }

  function clearSelection(): void {
    setSelected({});
  }

  function toggleAll(on: boolean): void {
    if (!on) {
      clearSelection();
      return;
    }
    const next: Record<string, boolean> = {};
    for (const r of userRows()) {
      if (r.SamAccountName) next[r.SamAccountName] = true;
    }
    setSelected(next);
  }

  /** Appelle ad_user_action sur un compte ; retourne true si succès. */
  async function callUserAction(
    sam: string,
    actionId: "enable" | "disable" | "unlock" | "reset-password",
    extra?: Record<string, unknown>,
  ): Promise<boolean> {
    if (!sam) return false;
    const params: Record<string, unknown> = { samAccountName: sam, action: actionId, ...extra };
    try {
      await tools.call("ad_user_action", params, { userConfirmedAt: new Date().toISOString() });
      return true;
    } catch (err) {
      setActionStatus({
        text: `Erreur action: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
      return false;
    }
  }

  async function runUserAction(
    sam: string,
    actionId: "enable" | "disable" | "unlock",
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    if (!sam) return;
    const ok = await confirm.confirm({
      title: `${label} - ${sam}`,
      body: `Confirmer l'action AD "${label}" sur l'utilisateur ${sam} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? sam : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label}...`, level: "info" });
    const success = await callUserAction(sam, actionId);
    if (success) {
      setActionStatus({ text: `Action ${label} sur ${sam} reussie.`, level: "info" });
      await usersQuery.refetch();
    }
  }

  /** Action de masse sur les comptes sélectionnés. */
  async function bulkAction(
    actionId: "enable" | "disable" | "unlock",
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    const predicate =
      actionId === "enable"
        ? (r: AdUser) => r.Enabled !== true
        : actionId === "disable"
          ? (r: AdUser) => r.Enabled !== false
          : (r: AdUser) => r.LockedOut !== false;
    const targets = selectedRows().filter(predicate);
    if (targets.length === 0) {
      setActionStatus({ text: "Aucun compte concerné par cette action.", level: "warn" });
      return;
    }
    const ok = await confirm.confirm({
      title: `${label} ${targets.length} compte${targets.length > 1 ? "s" : ""}`,
      body: `Confirmer l'action AD "${label}" en masse sur ${targets.length} utilisateur${targets.length > 1 ? "s" : ""} ?`,
      level,
      confirmLabel: label,
    });
    if (!ok) return;
    setBulkProgress({ done: 0, total: targets.length });
    let failures = 0;
    for (let i = 0; i < targets.length; i++) {
      const success = await callUserAction(targets[i]!.SamAccountName ?? "", actionId);
      if (!success) failures++;
      setBulkProgress({ done: i + 1, total: targets.length });
    }
    setBulkProgress(null);
    clearSelection();
    await usersQuery.refetch();
    setActionStatus(
      failures
        ? { text: `${label} terminé — ${failures} échec(s) sur ${targets.length}.`, level: "danger" }
        : { text: `${label} : ${targets.length} compte(s) traité(s).`, level: "info" },
    );
  }

  const userColumns: Column<AdUser>[] = [
    {
      id: "_select",
      enableSorting: false,
      header: () => (
        <span class="ua-check">
          <input
            type="checkbox"
            checked={userRows().length > 0 && userRows().every((r) => !!selected()[r.SamAccountName ?? ""])}
            title="Tout sélectionner"
            onChange={(e) => toggleAll(e.currentTarget.checked)}
          />
        </span>
      ),
      cell: (info) => {
        const sam = info.row.original.SamAccountName ?? "";
        return (
          <span class="ua-check">
            <input
              type="checkbox"
              checked={!!selected()[sam]}
              onChange={(e) => setRowSelected(sam, e.currentTarget.checked)}
            />
          </span>
        );
      },
    },
    { accessorKey: "SamAccountName", header: "SamAccountName" },
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "UserPrincipalName", header: "UserPrincipalName" },
    {
      accessorKey: "Enabled",
      header: "Enabled",
      cell: (info) => formatCell(info.getValue()),
    },
    {
      accessorKey: "LockedOut",
      header: "LockedOut",
      cell: (info) => formatCell(info.getValue()),
    },
    {
      accessorKey: "LastLogon",
      header: "LastLogon",
      cell: (info) => formatCell(info.getValue()),
    },
    { accessorKey: "Description", header: "Description" },
  ];

  const groupColumns: Column<AdGroup>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "SamAccountName", header: "SamAccountName" },
    { accessorKey: "Category", header: "Category" },
    { accessorKey: "Scope", header: "Scope" },
    { accessorKey: "Description", header: "Description" },
  ];

  const computerColumns: Column<AdComputer>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "DNSHostName", header: "DNSHostName" },
    { accessorKey: "OperatingSystem", header: "OperatingSystem" },
    { accessorKey: "OperatingSystemVersion", header: "OS Version" },
    {
      accessorKey: "Enabled",
      header: "Enabled",
      cell: (info) => formatCell(info.getValue()),
    },
    {
      accessorKey: "LastLogon",
      header: "LastLogon",
      cell: (info) => formatCell(info.getValue()),
    },
  ];

  const ouColumns: Column<AdOu>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "DistinguishedName", header: "DistinguishedName" },
    { accessorKey: "Description", header: "Description" },
    {
      accessorKey: "WhenCreated",
      header: "WhenCreated",
      cell: (info) => formatCell(info.getValue()),
    },
  ];

  const gpoColumns: Column<AdGpo>[] = [
    { accessorKey: "DisplayName", header: "DisplayName" },
    { accessorKey: "GpoStatus", header: "GpoStatus" },
    {
      accessorKey: "CreationTime",
      header: "CreationTime",
      cell: (info) => formatCell(info.getValue()),
    },
    {
      accessorKey: "ModificationTime",
      header: "ModificationTime",
      cell: (info) => formatCell(info.getValue()),
    },
    { accessorKey: "Owner", header: "Owner" },
  ];

  const toolbar = (
    <>
      <input
        type="text"
        class="panel-filter-input"
        placeholder="Filtre (nom partiel)..."
        spellcheck={false}
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <input
        type="number"
        class="panel-filter-input panel-filter-input--narrow"
        min="1"
        max="2000"
        title="Limite"
        value={limit()}
        onInput={(e) => {
          const n = Number(e.currentTarget.value);
          if (Number.isFinite(n) && n > 0) setLimit(n);
        }}
      />
      <button
        type="button"
        class="btn btn--xs btn--info"
        onClick={() => {
          usersQuery.refetch();
          groupsQuery.refetch();
          computersQuery.refetch();
          ousQuery.refetch();
          gposQuery.refetch();
        }}
      >
        Rechercher
      </button>
    </>
  );

  const subtabs: SubTab[] = [
    {
      id: "users",
      label: "Utilisateurs",
      render: () => (
        <>
          <Show when={selectedRows().length > 0}>
            <div class="ua-bulkbar">
              <span class="ua-bulkbar__count">{selectedRows().length} sélectionné(s)</span>
              <button
                type="button"
                class="btn btn--xs btn--ghost"
                disabled={!!bulkProgress()}
                onClick={() => bulkAction("unlock", "Deverrouiller", "info")}
              >
                Deverrouiller
              </button>
              <button
                type="button"
                class="btn btn--xs btn--warn"
                disabled={!!bulkProgress()}
                onClick={() => bulkAction("enable", "Activer", "warn")}
              >
                Activer
              </button>
              <button
                type="button"
                class="btn btn--xs btn--danger"
                disabled={!!bulkProgress()}
                onClick={() => bulkAction("disable", "Desactiver", "danger")}
              >
                Desactiver
              </button>
              <span class="ua-bulkbar__spacer" />
              <button type="button" class="btn btn--xs btn--ghost" onClick={clearSelection}>
                Effacer
              </button>
              <Show when={bulkProgress()}>
                {(p) => (
                  <>
                    <div class="ua-bulkbar__progress">
                      <div
                        class="ua-bulkbar__progress-fill"
                        style={{ width: `${(p().done / Math.max(1, p().total)) * 100}%` }}
                      />
                    </div>
                    <span class="ua-bulkbar__progress-label">
                      {p().done} / {p().total}
                    </span>
                  </>
                )}
              </Show>
            </div>
          </Show>
          <DataTable<AdUser>
            rows={userRows()}
            columns={userColumns}
            rowActions={(r) => (
              <>
                <button
                  type="button"
                  class="btn btn--xs btn--warn"
                  disabled={r.Enabled === true}
                  onClick={() => runUserAction(r.SamAccountName ?? "", "enable", "Activer", "warn")}
                >
                  Activer
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--danger"
                  disabled={r.Enabled === false}
                  onClick={() => runUserAction(r.SamAccountName ?? "", "disable", "Desactiver", "danger")}
                >
                  Desactiver
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--ghost"
                  disabled={r.LockedOut === false}
                  onClick={() => runUserAction(r.SamAccountName ?? "", "unlock", "Deverrouiller", "info")}
                >
                  Deverrouiller
                </button>
                <button type="button" class="btn btn--xs btn--danger" onClick={() => setPwdTarget(r)}>
                  Reset MDP
                </button>
              </>
            )}
          />
        </>
      ),
    },
    {
      id: "groups",
      label: "Groupes",
      render: () => <DataTable<AdGroup> rows={groupsQuery.data?.rows ?? []} columns={groupColumns} />,
    },
    {
      id: "computers",
      label: "Ordinateurs",
      render: () => <DataTable<AdComputer> rows={computersQuery.data?.rows ?? []} columns={computerColumns} />,
    },
    {
      id: "ous",
      label: "OU",
      render: () => <DataTable<AdOu> rows={ousQuery.data?.rows ?? []} columns={ouColumns} />,
    },
    {
      id: "gpos",
      label: "GPO",
      render: () => <DataTable<AdGpo> rows={gposQuery.data?.rows ?? []} columns={gpoColumns} />,
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (usersQuery.isFetching) return { text: "Chargement...", level: "info" };
    if (usersQuery.error)
      return {
        text: `Erreur: ${usersQuery.error instanceof Error ? usersQuery.error.message : String(usersQuery.error)}`,
        level: "danger",
      };
    return undefined;
  };

  return (
    <PanelChrome icon="👤" title="AD Tools" tagType="helper" actions={toolbar}>
      <PanelLayout title="Active Directory" subtabs={subtabs} status={status()} />
      <ResetPasswordDrawer
        user={pwdTarget()}
        onClose={() => setPwdTarget(null)}
        onDone={(msg, level) => {
          setActionStatus({ text: msg, level });
          if (level !== "danger") void usersQuery.refetch();
        }}
        confirmFn={(sam) =>
          confirm.confirm({
            title: `Reset MDP - ${sam}`,
            body: `Confirmer la réinitialisation du mot de passe AD de ${sam} ?`,
            level: "danger",
            confirmLabel: "Reset MDP",
            entityName: sam,
          })
        }
        runFn={(sam, pwd) => callUserAction(sam, "reset-password", { newPassword: pwd, requireChangeAtLogon: true })}
        fetchProps={(sam) => tools.call<Record<string, unknown>>("ad_user_get", { samAccountName: sam })}
      />
    </PanelChrome>
  );
};

/** Drawer de réinitialisation de mot de passe AD — affiche les propriétés du compte. */
function ResetPasswordDrawer(props: {
  user: AdUser | null;
  onClose: () => void;
  onDone: (msg: string, level: "info" | "warn" | "danger") => void | Promise<void>;
  confirmFn: (sam: string) => Promise<boolean>;
  runFn: (sam: string, pwd: string) => Promise<boolean>;
  fetchProps: (sam: string) => Promise<Record<string, unknown>>;
}) {
  const [pwd, setPwd] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [details, setDetails] = createSignal<Record<string, unknown> | null>(null);
  const [loadingDetails, setLoadingDetails] = createSignal(false);

  const sam = (): string => props.user?.SamAccountName ?? "";

  // Recharge les propriétés du compte à chaque ouverture du drawer.
  createEffect(() => {
    const s = sam();
    setPwd("");
    setError(null);
    setDetails(null);
    if (!s) return;
    setLoadingDetails(true);
    props
      .fetchProps(s)
      .then((d) => setDetails(d))
      .catch(() => setDetails(null))
      .finally(() => setLoadingDetails(false));
  });

  async function submit(): Promise<void> {
    setError(null);
    const value = pwd();
    if (!PASSWORD_RE.test(value)) {
      setError("Mot de passe invalide (8-256 caractères ASCII imprimables).");
      return;
    }
    const ok = await props.confirmFn(sam());
    if (!ok) return;
    setBusy(true);
    void props.onDone(`Réinitialisation du mot de passe de ${sam()}...`, "info");
    const success = await props.runFn(sam(), value);
    setBusy(false);
    if (success) {
      void props.onDone(`Mot de passe de ${sam()} réinitialisé.`, "info");
      props.onClose();
    } else {
      setError("Échec de la réinitialisation.");
    }
  }

  return (
    <Drawer open={!!props.user} onClose={props.onClose} title="Réinitialiser le mot de passe" width="440px">
      <Show when={props.user}>
        {(user) => (
          <div class="ua-form">
            <div class="ua-props">
              <h3 class="ua-props__title">Compte concerné</h3>
              <Show when={!loadingDetails()} fallback={<div class="ua-props__loading">Chargement des propriétés…</div>}>
                <div class="ua-props__grid">
                  <For each={buildPropEntries(user(), details())}>
                    {([k, v]) => (
                      <>
                        <span class="ua-props__key">{k}</span>
                        <span class="ua-props__val">{v}</span>
                      </>
                    )}
                  </For>
                </div>
              </Show>
            </div>
            <div class="ua-form__field">
              <span class="ua-form__label">Nouveau mot de passe</span>
              <input
                class="ua-inline__input"
                type="password"
                autocomplete="new-password"
                placeholder="8-256 caractères ASCII"
                value={pwd()}
                onInput={(e) => setPwd(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void submit();
                }}
              />
              <span class="ua-form__hint">Le compte devra changer ce mot de passe à la prochaine connexion.</span>
            </div>
            <Show when={error()}>
              <div class="ua-form__error">{error()}</div>
            </Show>
            <div class="ua-form__actions">
              <button type="button" class="btn btn--sm btn--ghost" onClick={props.onClose}>
                Annuler
              </button>
              <button type="button" class="btn btn--sm btn--danger" disabled={busy()} onClick={submit}>
                {busy() ? "Application..." : "Réinitialiser"}
              </button>
            </div>
          </div>
        )}
      </Show>
    </Drawer>
  );
}

/** Construit la liste des propriétés affichées (fusionne la ligne et ad_user_get). */
function buildPropEntries(user: AdUser, details: Record<string, unknown> | null): [string, string][] {
  const src = details ?? {};
  const pick = (k: string, fallback?: unknown): string => {
    const v = src[k] ?? src[k.toLowerCase()] ?? fallback;
    if (v == null || v === "") return "—";
    return String(v);
  };
  return [
    ["SamAccountName", user.SamAccountName || "—"],
    ["Nom", pick("name", user.Name)],
    ["UPN", pick("userPrincipalName", user.UserPrincipalName)],
    ["Activé", pick("enabled", user.Enabled)],
    ["Verrouillé", pick("lockedOut", user.LockedOut)],
    ["Description", pick("description", user.Description)],
  ];
}

registerPanel("ad", AdPanel);
export default AdPanel;
